// A push. A piece with `pushes` that moves into an occupied square moves the
// occupant to an empty square beside it and takes its square. The move
// generator and applyMove both ask here, so they agree.
//
// The occupant goes straight on, in the direction of the move, when that
// square is free. When it is not, the occupant goes to the free square
// beside it that is nearest to that direction. Rob, 2026-10-13: "Does knock
// knock push into any adjacent space or just the one directly behind? I think
// adjacent should count." Until then only the square straight on counted.
// A rook pushes as any other piece does: "we should have it applicable to
// rooks, too". Until then a rook with `pushes` captured as before.

import { fileOf, neighborSquares, rankOf } from './squares';
import { isSolid } from './terrain';
import type { Move, Position } from './types';

/**
 * Where the occupant of `move.to` goes when the move pushes it. Null when the
 * move is not a push: the mover does not push, the square is empty, or no
 * square beside the occupant is free. The square the mover leaves is not one
 * of them: the two do not trade places. No random draw picks the square, so
 * the search and the game find the same one.
 */
export const pushTarget = (position: Position, move: Move): number | null => {
  const mover = position.board[move.from];
  if (!mover?.pushes || !position.board[move.to]) return null;
  const free = neighborSquares(move.to).filter(
    (square) =>
      square !== move.from && position.board[square] === null && !isSolid(position.rules, square),
  );
  if (free.length === 0) return null;

  // The direction of the move, one step. For a knight it is the diagonal the jump leans to.
  const stepFile = Math.sign(fileOf(move.to) - fileOf(move.from));
  const stepRank = Math.sign(rankOf(move.to) - rankOf(move.from));
  const stepLength = Math.hypot(stepFile, stepRank) || 1;
  /** How far the square is turned from straight on: 1 is straight on, -1 is straight back. */
  const alignment = (square: number): number => {
    const file = fileOf(square) - fileOf(move.to);
    const rank = rankOf(square) - rankOf(move.to);
    return (file * stepFile + rank * stepRank) / (Math.hypot(file, rank) * stepLength);
  };
  // Two squares at the same turn, one at each side: the one with the lower number.
  const [nearest] = free
    .map((square) => ({ square, alignment: alignment(square) }))
    .sort((a, b) => b.alignment - a.alignment || a.square - b.square);
  return nearest?.square ?? null;
};
